import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { BackButton } from '@/components/ui/back-button'
import {
  Search,
  Filter,
  Package,
  User,
  Calendar,
  CheckCircle,
  XCircle,
  Clock,
  AlertCircle,
  MapPin,
  DollarSign,
  Truck,
  Phone,
  Eye,
  ArrowRight
} from 'lucide-react'

interface DeliveryRequest {
  id: string
  order_id: string
  seller_id: string
  delivery_partner_id: string | null
  status: string
  current_step: string
  pickup_instructions: string | null
  delivery_notes: string | null
  created_at: string
  updated_at: string
  step_history: {
    step: string
    timestamp: string
    metadata: any
  }[]
  order: {
    id: string
    buyer_id: string
    final_price: number
    delivery_type: string
    delivery_address: string
    product: {
      title: string
      images: string[]
    }
    buyer: {
      full_name: string | null
      phone_number: string | null
    }
    seller: {
      full_name: string | null
    }
  }
  delivery_partner?: {
    id: string
    applicant_name: string
    applicant_email: string
    vehicle_type: string
  } | null
}

export function ManagementDeliveriesPage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const [deliveries, setDeliveries] = useState<DeliveryRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = useState<'all' | 'pending' | 'active' | 'completed'>('all')
  const [searchQuery, setSearchQuery] = useState('')
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [selectedDelivery, setSelectedDelivery] = useState<DeliveryRequest | null>(null)
  const [showDeliveryDetails, setShowDeliveryDetails] = useState(false)

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    if (user.role !== 'manager') {
      navigate('/')
      return
    }

    fetchDeliveries()
  }, [user, navigate, filter, searchQuery])

  const fetchDeliveries = async () => {
    try {
      setLoading(true)
      let query = supabase
        .from('delivery_requests')
        .select(`
          *,
          order:orders (
            id,
            buyer_id,
            final_price,
            delivery_type,
            delivery_address,
            product:products (
              title,
              images
            ),
            buyer:profiles!buyer_id (
              full_name,
              phone_number
            ),
            seller:profiles!seller_id (
              full_name
            )
          ),
          delivery_partner:delivery_partners (
            id,
            applicant_name,
            applicant_email,
            vehicle_type
          )
        `)
        .eq('manager_id', user.id)
        .order('created_at', { ascending: false })

      // Apply status filter
      if (filter === 'pending') {
        query = query.eq('status', 'pending')
      } else if (filter === 'active') {
        query = query.eq('status', 'accepted')
      } else if (filter === 'completed') {
        query = query.in('current_step', ['delivered', 'cancelled'])
      }

      // Apply search filter
      if (searchQuery) {
        query = query.or(
          `order.product.title.ilike.%${searchQuery}%,order.buyer.full_name.ilike.%${searchQuery}%,order.seller.full_name.ilike.%${searchQuery}%`
        )
      }

      const { data, error } = await query
      if (error) throw error

      setDeliveries(data || [])
    } catch (err) {
      console.error('Error fetching deliveries:', err)
      setToastMessage('Erreur lors du chargement des livraisons')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const assignDeliveryPartner = async (deliveryId: string, partnerId: string) => {
    try {
      const { error } = await supabase
        .from('delivery_requests')
        .update({
          delivery_partner_id: partnerId,
          status: 'accepted',
          current_step: 'accepted',
          step_metadata: {
            assigned_at: new Date().toISOString(),
            assigned_by: user?.id
          }
        })
        .eq('id', deliveryId)

      if (error) throw error

      setToastMessage('Livreur assigné avec succès')
      setToastType('success')
      setShowToast(true)
      fetchDeliveries()
    } catch (err) {
      console.error('Error assigning delivery partner:', err)
      setToastMessage('Erreur lors de l\'assignation du livreur')
      setToastType('error')
      setShowToast(true)
    }
  }

  const getStepLabel = (step: string) => {
    switch (step) {
      case 'pending': return 'En attente'
      case 'accepted': return 'Acceptée'
      case 'preparing': return 'En préparation'
      case 'pickup_ready': return 'Prêt pour récupération'
      case 'picked_up': return 'Récupéré'
      case 'in_transit': return 'En transit'
      case 'arrived': return 'Arrivé'
      case 'delivered': return 'Livré'
      case 'cancelled': return 'Annulé'
      default: return step
    }
  }

  const getStepIcon = (step: string) => {
    switch (step) {
      case 'pending': return <Clock className="h-5 w-5 text-yellow-500" />
      case 'accepted': return <CheckCircle className="h-5 w-5 text-blue-500" />
      case 'preparing': return <Package className="h-5 w-5 text-purple-500" />
      case 'pickup_ready': return <Package className="h-5 w-5 text-indigo-500" />
      case 'picked_up': return <Truck className="h-5 w-5 text-orange-500" />
      case 'in_transit': return <Truck className="h-5 w-5 text-blue-500" />
      case 'arrived': return <MapPin className="h-5 w-5 text-green-500" />
      case 'delivered': return <CheckCircle className="h-5 w-5 text-green-500" />
      case 'cancelled': return <XCircle className="h-5 w-5 text-red-500" />
      default: return <Clock className="h-5 w-5 text-gray-500" />
    }
  }

  const getNextStep = (currentStep: string) => {
    const steps = [
      'pending',
      'accepted',
      'preparing',
      'pickup_ready',
      'picked_up',
      'in_transit',
      'arrived',
      'delivered'
    ]
    
    const currentIndex = steps.indexOf(currentStep)
    if (currentIndex === -1 || currentIndex === steps.length - 1) return null
    
    return steps[currentIndex + 1]
  }

  const updateDeliveryStep = async (deliveryId: string, step: string) => {
    try {
      const { error } = await supabase
        .from('delivery_requests')
        .update({
          current_step: step,
          step_metadata: {
            updated_at: new Date().toISOString(),
            updated_by: user?.id,
            step
          }
        })
        .eq('id', deliveryId)

      if (error) throw error

      // If the step is delivered, also update the order status
      if (step === 'delivered') {
        const delivery = deliveries.find(d => d.id === deliveryId)
        if (delivery) {
          await supabase
            .from('orders')
            .update({ status: 'delivered' })
            .eq('id', delivery.order_id)
        }
      }

      setToastMessage('Étape mise à jour avec succès')
      setToastType('success')
      setShowToast(true)
      setShowDeliveryDetails(false)
      fetchDeliveries()
    } catch (err) {
      console.error('Error updating delivery step:', err)
      setToastMessage('Erreur lors de la mise à jour de l\'étape')
      setToastType('error')
      setShowToast(true)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      {/* Delivery Details Modal */}
      {showDeliveryDetails && selectedDelivery && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start">
                <h3 className="text-lg font-medium text-gray-900">
                  Détails de la Livraison
                </h3>
                <button
                  onClick={() => setShowDeliveryDetails(false)}
                  className="text-gray-400 hover:text-gray-500"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>

              <div className="mt-6 space-y-6">
                {/* Order Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Informations de la Commande</h4>
                  <div className="mt-2 flex items-center">
                    <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                      <img
                        src={selectedDelivery.order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                        alt={selectedDelivery.order.product?.title}
                        className="h-full w-full object-cover"
                      />
                    </div>
                    <div className="ml-4">
                      <h3 className="text-sm font-medium text-gray-900">
                        {selectedDelivery.order.product?.title}
                      </h3>
                      <div className="mt-1 flex items-center text-sm text-gray-500">
                        <DollarSign className="h-4 w-4 mr-1" />
                        {selectedDelivery.order.final_price.toFixed(2)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Seller Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Vendeur</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedDelivery.order.seller?.full_name || 'Vendeur'}
                    </p>
                    <div className="mt-1 text-sm text-gray-500">
                      {selectedDelivery.pickup_instructions}
                    </div>
                  </div>
                </div>

                {/* Buyer Info */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Client</h4>
                  <div className="mt-2">
                    <p className="text-sm font-medium text-gray-900">
                      {selectedDelivery.order.buyer?.full_name || 'Client'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {selectedDelivery.order.buyer?.phone_number}
                    </p>
                    <div className="mt-1 text-sm text-gray-500">
                      <MapPin className="h-4 w-4 inline mr-1" />
                      {selectedDelivery.order.delivery_address}
                    </div>
                    <div className="mt-1 text-sm text-gray-500">
                      {selectedDelivery.delivery_notes}
                    </div>
                  </div>
                </div>

                {/* Delivery Partner */}
                {selectedDelivery.delivery_partner && (
                  <div>
                    <h4 className="text-sm font-medium text-gray-500">Livreur</h4>
                    <div className="mt-2">
                      <div className="flex items-center">
                        <div className="h-8 w-8 rounded-full bg-gray-200 flex items-center justify-center">
                          <User className="h-4 w-4 text-gray-500" />
                        </div>
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {selectedDelivery.delivery_partner.applicant_name}
                          </p>
                          <p className="text-sm text-gray-500">
                            {selectedDelivery.delivery_partner.applicant_email}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Current Status */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Statut Actuel</h4>
                  <div className="mt-2 flex items-center">
                    {getStepIcon(selectedDelivery.current_step)}
                    <span className="ml-2 text-sm font-medium text-gray-900">
                      {getStepLabel(selectedDelivery.current_step)}
                    </span>
                  </div>
                </div>

                {/* Delivery Steps */}
                <div>
                  <h4 className="text-sm font-medium text-gray-500">Étapes de la Livraison</h4>
                  <div className="mt-4 relative">
                    <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-200"></div>
                    <div className="space-y-6">
                      {['pending', 'accepted', 'preparing', 'pickup_ready', 'picked_up', 'in_transit', 'arrived', 'delivered'].map((step, index) => {
                        const isCompleted = ['delivered', 'cancelled'].includes(selectedDelivery.current_step) 
                          ? index <= ['pending', 'accepted', 'preparing', 'pickup_ready', 'picked_up', 'in_transit', 'arrived', 'delivered'].indexOf(selectedDelivery.current_step)
                          : index < ['pending', 'accepted', 'preparing', 'pickup_ready', 'picked_up', 'in_transit', 'arrived', 'delivered'].indexOf(selectedDelivery.current_step);
                        const isCurrent = selectedDelivery.current_step === step;
                        
                        return (
                          <div key={step} className="relative flex items-start">
                            <div className={`absolute left-0 h-9 w-9 rounded-full flex items-center justify-center ${
                              isCompleted 
                                ? 'bg-primary text-white' 
                                : isCurrent
                                  ? 'bg-white border-2 border-primary text-primary'
                                  : 'bg-white border-2 border-gray-200 text-gray-400'
                            }`}>
                              {getStepIcon(step)}
                            </div>
                            <div className="ml-12">
                              <h3 className={`text-base font-medium ${
                                isCompleted || isCurrent ? 'text-gray-900' : 'text-gray-500'
                              }`}>
                                {getStepLabel(step)}
                              </h3>
                              {isCurrent && (
                                <p className="mt-1 text-sm text-primary">
                                  Étape actuelle
                                </p>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Next Step Button */}
                <div className="pt-6 border-t flex justify-end space-x-3">
                  <Button
                    variant="secondary"
                    onClick={() => setShowDeliveryDetails(false)}
                  >
                    Fermer
                  </Button>
                  
                  {getNextStep(selectedDelivery.current_step) && (
                    <Button
                      variant="primary"
                      onClick={() => updateDeliveryStep(
                        selectedDelivery.id, 
                        getNextStep(selectedDelivery.current_step) || ''
                      )}
                    >
                      {getStepLabel(getNextStep(selectedDelivery.current_step) || '')}
                      <ArrowRight className="h-4 w-4 ml-2" />
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <div className="flex items-center">
            <BackButton to="/management" label="Retour" />
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                Gestion des Livraisons
              </h1>
              <p className="mt-1 text-sm text-gray-500">
                Suivez et gérez les livraisons assignées à votre équipe
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row justify-between items-stretch sm:items-center space-y-4 sm:space-y-0 sm:space-x-4 mb-8">
              <div className="flex-1 max-w-lg">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <Search className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Rechercher une livraison..."
                    className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
              </div>
              <div className="flex items-center space-x-4">
                <select
                  value={filter}
                  onChange={(e) => setFilter(e.target.value as typeof filter)}
                  className="block w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-primary focus:border-primary"
                >
                  <option value="all">Tous les statuts</option>
                  <option value="pending">En attente</option>
                  <option value="active">En cours</option>
                  <option value="completed">Terminées</option>
                </select>
              </div>
            </div>

            {loading ? (
              <div className="flex justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : deliveries.length === 0 ? (
              <div className="text-center py-12">
                <Truck className="mx-auto h-12 w-12 text-gray-400" />
                <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune livraison</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Aucune livraison n'a été assignée à votre équipe pour le moment
                </p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Produit
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                        Client
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                        Livreur
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden lg:table-cell">
                        Adresse
                      </th>
                      <th scope="col" className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Statut
                      </th>
                      <th scope="col" className="relative px-4 sm:px-6 py-3">
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {deliveries.map((delivery) => (
                      <tr key={delivery.id}>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            <div className="h-10 w-10 flex-shrink-0">
                              <img
                                src={delivery.order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                                alt={delivery.order.product?.title}
                                className="h-10 w-10 rounded object-cover"
                              />
                            </div>
                            <div className="ml-4">
                              <div className="text-sm font-medium text-gray-900 line-clamp-1">
                                {delivery.order.product?.title}
                              </div>
                              <div className="text-xs text-gray-500">
                                #{delivery.order_id.slice(0, 8)}
                              </div>
                            </div>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden sm:table-cell">
                          <div className="text-sm text-gray-900">
                            {delivery.order.buyer?.full_name || 'Client'}
                          </div>
                          <div className="text-xs text-gray-500">
                            {delivery.order.buyer?.phone_number}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                          {delivery.delivery_partner ? (
                            <div className="text-sm text-gray-900">
                              {delivery.delivery_partner.applicant_name}
                            </div>
                          ) : (
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                              Non assigné
                            </span>
                          )}
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden lg:table-cell">
                          <div className="text-sm text-gray-500 truncate max-w-xs">
                            {delivery.order.delivery_address}
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center">
                            {getStepIcon(delivery.current_step)}
                            <span className="ml-2 text-sm text-gray-900 hidden sm:inline">
                              {getStepLabel(delivery.current_step)}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setSelectedDelivery(delivery)
                              setShowDeliveryDetails(true)
                            }}
                          >
                            <Eye className="h-4 w-4 sm:mr-2" />
                            <span className="hidden sm:inline">Détails</span>
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}